"use client";

import { useQuery } from "@tanstack/react-query";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";

interface EmailsSentParams {
  email?: string; // Defaults to current user email
  fromDate: Date;
  toDate: Date;
  limit?: number; // Cap result size from API; defaults to 1000
}

interface TimelineMessageLike {
  direction?: string; // "outgoing" | "incoming" | "sent" | "received"
  from?: string | string[];
  to?: string | string[];
  headers?: Record<string, any>;
}

function isOutgoingForEmail(message: TimelineMessageLike, senderEmail: string): boolean {
  const direction = (message.direction || "").toLowerCase();
  if (direction === "outgoing" || direction === "sent") return true;

  // Heuristics if direction not provided
  const fromField = message.from;
  if (typeof fromField === "string") {
    return fromField.toLowerCase().includes(senderEmail.toLowerCase());
  }
  if (Array.isArray(fromField)) {
    return fromField.some(
      (v) => typeof v === "string" && v.toLowerCase().includes(senderEmail.toLowerCase())
    );
  }
  return false;
}

export function useEmailsSentCount({ email, fromDate, toDate, limit = 1000 }: EmailsSentParams) {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const effectiveEmail = email || appUser?.email || "";

  return useQuery({
    queryKey: [
      "emails-sent-count",
      { email: effectiveEmail, from: fromDate.toISOString(), to: toDate.toISOString(), limit },
    ],
    enabled: isAuthenticated && !!effectiveEmail && !!fromDate && !!toDate,
    queryFn: async (): Promise<number> => {
      const params = new URLSearchParams({
        email: effectiveEmail,
        limit: String(limit),
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
      });

      const res = await fetch(`/api/connections/messages/timeline?${params.toString()}`, {
        method: "GET",
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to fetch timeline messages: ${res.status} ${text}`);
      }
      const messages: TimelineMessageLike[] = await res.json();
      return messages.reduce(
        (acc, m) => (isOutgoingForEmail(m, effectiveEmail) ? acc + 1 : acc),
        0
      );
    },
    staleTime: 60 * 1000,
  });
}
